import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../engine/api/context';
import { ApiError } from '../../engine/api/client';
import { Button } from '../../engine/design/primitives/Button';
import { Field } from '../../engine/design/primitives/Input';
import { DatePicker } from '../../engine/design/primitives/DatePicker';
import { Banner, EmptyState, Money, Skeleton } from '../../engine/design/primitives/Misc';
import { useToast } from '../../engine/design/primitives/Toast';
import { MoneyEditor } from '../../engine/fields/registry';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { formatDate } from '../../lib/format';
import type { ScreenContext } from '../../engine/screens/types';
import type { TechnicianCostRate } from '../../types';

// Technician detail tab: effective-dated cost-rate history (GET/POST /technicians/:id/cost-rates).
// Rates are resolved for the work date and snapshotted onto entries, so history is never
// edited — only a new effective-from row is added.

const COST_FIELD = { name: 'cost_rate_minor', label: 'Hourly cost rate', type: 'money' as const, required: true, readOnly: false, nullable: false, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: false, inList: false, inDetail: false, currencyField: 'currency' };

export function TechnicianCostRates({ id, row, refresh }: ScreenContext) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const currency = (row?.currency as string | undefined) ?? 'USD';
  const key = ['technician-cost-rates', id];
  const rates = useQuery({ queryKey: key, queryFn: () => api.request<TechnicianCostRate[]>(`/v1/technicians/${id}/cost-rates`), enabled: Boolean(id) });
  const [from, setFrom] = useState<string | null>(null);
  const [minor, setMinor] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => api.request(`/v1/technicians/${id}/cost-rates`, { method: 'POST', body: { effective_from: from, cost_rate_minor: minor } }),
    onSuccess: () => {
      setFrom(null);
      setMinor(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: key });
      refresh();
      toast({ title: 'Cost rate added', detail: 'Effective-dated; historical entries keep their snapshot.' });
    },
    onError: (e) => setError(e instanceof ApiError ? e.detail : String(e)),
  });

  const columns: GridColumn<TechnicianCostRate>[] = [
    { id: 'effective_from', header: 'Effective from', cell: (r) => formatDate(r.effective_from), width: 150 },
    { id: 'effective_to', header: 'Effective to', cell: (r) => (r.effective_to ? formatDate(r.effective_to) : <span className="hf-muted">open</span>), width: 150 },
    { id: 'cost_rate_minor', header: 'Cost / h', align: 'right', cell: (r) => <Money minor={r.cost_rate_minor} currency={r.currency} />, width: 130 },
    { id: 'note', header: 'Note', cell: (r) => r.note ?? <span className="hf-muted">—</span> },
  ];

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-5)' }}>
      <form
        className="hf-row"
        style={{ alignItems: 'flex-end', gap: 'var(--s-3)', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (from && minor != null && minor > 0) add.mutate();
        }}
      >
        <Field label="Effective from" required>
          <DatePicker value={from} onChange={setFrom} aria-label="Effective from" />
        </Field>
        <Field label="Hourly cost rate" required>
          <MoneyEditor field={COST_FIELD} value={minor} onChange={(v) => setMinor(v as number | null)} currency={currency} aria-label="Hourly cost rate" />
        </Field>
        <Button type="submit" variant="primary" icon="plus" busy={add.isPending} disabled={!from || minor == null || minor <= 0}>
          Add rate
        </Button>
      </form>
      {error && <Banner tone="error">{error}</Banner>}
      {rates.isLoading ? (
        <Skeleton lines={3} />
      ) : (
        <DataGrid<TechnicianCostRate>
          columns={columns}
          rows={rates.data ?? []}
          keyOf={(r) => r.id}
          hideToolbar
          height={280}
          empty={<EmptyState title="No effective-dated cost rates yet" hint="The technician's default cost rate applies until one is added." icon="clock" />}
        />
      )}
    </div>
  );
}
